import { createContext, useCallback, useRef, useMemo, useEffect } from 'react';
import type { Socket } from 'socket.io-client';
import { useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';
import { useMessagingStore } from '@/store/messaging.store';
import { useSocketFactory } from '@/hooks/useSocketFactory';
import { playBeep } from '@/utils/audioAlerts';
import { toast } from '@/utils/toast';
import type {
  MessageWithDetails,
  SocketMessagePayload,
  SocketTypingPayload,
  SocketReactionPayload,
  SocketReadPayload,
  SocketConversationUpdatePayload,
} from '@/types/messaging';

// ---------------------------------------------------------------------------
// Context type
// ---------------------------------------------------------------------------

export interface MessagingSocketContextValue {
  socket: Socket | null;
  joinConversation: (conversationId: string) => void;
  leaveConversation: (conversationId: string) => void;
  sendMessage: (data: {
    conversation_id: string;
    content?: string;
    type?: 'text' | 'file' | 'system';
    reply_to_id?: string;
    metadata?: Record<string, unknown>;
  }) => void;
  editMessage: (data: { message_id: string; conversation_id: string; content: string }) => void;
  deleteMessage: (data: { message_id: string; conversation_id: string }) => void;
  startTyping: (conversationId: string) => void;
  stopTyping: (conversationId: string) => void;
  markAsRead: (data: { message_id: string; conversation_id: string }) => void;
  markConversationAsRead: (conversationId: string) => void;
  toggleReaction: (data: { message_id: string; conversation_id: string; emoji: string }) => void;
}

export const MessagingSocketContext = createContext<MessagingSocketContextValue | null>(null);

// ---------------------------------------------------------------------------
// Event handlers (pure functions using direct store access)
// ---------------------------------------------------------------------------

function handleMessageNew(payload: SocketMessagePayload, socketRef: React.MutableRefObject<Socket | null>) {
  if (!payload?.conversation_id || !payload?.message?.id) {
    console.warn('[Messaging] Invalid message:new payload', payload);
    return;
  }

  const store = useMessagingStore.getState();
  const currentUserId = useAuthStore.getState().user?.id;

  store.addMessage(payload.conversation_id, payload.message);

  store.updateConversation(payload.conversation_id, {
    last_message_at: payload.message.created_at,
    last_message_preview: payload.message.content?.substring(0, 100),
  });

  // Increment unread if not the active conversation and not from current user
  if (
    payload.conversation_id !== store.activeConversationId &&
    payload.message.sender_id !== currentUserId
  ) {
    store.incrementUnreadCount(payload.conversation_id);
  }

  // Acknowledge delivery for messages from other users
  if (payload.message.sender_id !== currentUserId) {
    socketRef.current?.emit('message:delivered', {
      message_id: payload.message.id,
      conversation_id: payload.conversation_id,
    });
  }
}

function handleMessageDelivered(payload: {
  conversation_id: string;
  message_id: string;
  user_id: string;
  delivered_at: string;
}) {
  if (!payload?.conversation_id || !payload?.message_id || !payload?.user_id) return;
  const store = useMessagingStore.getState();
  const messages = store.messagesByConversation[payload.conversation_id] || [];
  const message = messages.find((m) => m.id === payload.message_id);
  if (message) {
    const currentDelivered = (message.metadata?.delivered_to as string[] | undefined) || [];
    if (!currentDelivered.includes(payload.user_id)) {
      store.updateMessage(payload.conversation_id, payload.message_id, {
        metadata: {
          ...message.metadata,
          delivered_to: [...currentDelivered, payload.user_id],
        },
      });
    }
  }
}

function handleMessageUpdated(payload: { conversation_id: string; message: MessageWithDetails }) {
  useMessagingStore.getState().updateMessage(payload.conversation_id, payload.message.id, payload.message);
}

function handleMessageDeleted(payload: { conversation_id: string; message_id: string }) {
  useMessagingStore.getState().removeMessage(payload.conversation_id, payload.message_id);
}

function handleMessageRead(payload: SocketReadPayload) {
  const store = useMessagingStore.getState();
  const messages = store.messagesByConversation[payload.conversation_id] || [];
  const message = messages.find((m) => m.id === payload.message_id);
  if (message) {
    const currentReadBy = message.read_by || [];
    // Avoid duplicate read receipts
    if (!currentReadBy.some((r) => r.user_id === payload.user_id)) {
      store.updateMessage(payload.conversation_id, payload.message_id, {
        read_by: [
          ...currentReadBy,
          {
            id: `${payload.message_id}-${payload.user_id}`,
            message_id: payload.message_id,
            user_id: payload.user_id,
            tenant_id: '',
            read_at: payload.read_at,
          },
        ],
      });
    }
  }
}

function handleReactionAdded(payload: SocketReactionPayload) {
  const store = useMessagingStore.getState();
  const messages = store.messagesByConversation[payload.conversation_id] || [];
  const message = messages.find((m) => m.id === payload.message_id);
  if (message) {
    const currentReactions = message.reactions || [];
    // Avoid duplicate reactions
    if (!currentReactions.some((r) => r.id === payload.reaction.id)) {
      store.updateMessage(payload.conversation_id, payload.message_id, {
        reactions: [...currentReactions, payload.reaction],
      });
    }
  }
}

function handleReactionRemoved(payload: {
  conversation_id: string;
  message_id: string;
  user_id: string;
  emoji: string;
}) {
  const store = useMessagingStore.getState();
  const messages = store.messagesByConversation[payload.conversation_id] || [];
  const message = messages.find((m) => m.id === payload.message_id);
  if (message) {
    const currentReactions = message.reactions || [];
    store.updateMessage(payload.conversation_id, payload.message_id, {
      reactions: currentReactions.filter(
        (r) => !(r.user_id === payload.user_id && r.emoji === payload.emoji),
      ),
    });
  }
}

function handleConversationUpdated(payload: SocketConversationUpdatePayload) {
  const store = useMessagingStore.getState();
  store.updateConversation(payload.conversation_id, {
    last_message_at: payload.last_message_at,
    last_message_preview: payload.last_message_preview,
  });
  if (payload.unread_increment && payload.conversation_id !== store.activeConversationId) {
    store.incrementUnreadCount(payload.conversation_id);
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function MessagingSocketProvider({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const activeTenant = useTenantStore((s) => s.activeTenant);

  // Typing timeout management
  const typingTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  // We need the socketRef available inside event handlers that reference it
  // (e.g. message:new emits message:delivered). We'll get it from useSocketFactory.
  // To avoid a chicken-and-egg problem we use a stable ref that the events object
  // can close over and that we update once useSocketFactory returns.
  const stableSocketRef = useRef<Socket | null>(null);

  const handleTyping = useCallback((payload: SocketTypingPayload) => {
    const currentUserId = useAuthStore.getState().user?.id;
    if (payload.user_id === currentUserId) return;

    useMessagingStore.getState().addTypingUser(payload.conversation_id, {
      user_id: payload.user_id,
      user_name: payload.user_name,
      started_at: new Date().toISOString(),
    });

    // Auto-remove typing indicator after 5 seconds
    const key = `${payload.conversation_id}:${payload.user_id}`;
    if (typingTimeoutsRef.current[key]) {
      clearTimeout(typingTimeoutsRef.current[key]);
    }
    typingTimeoutsRef.current[key] = setTimeout(() => {
      useMessagingStore.getState().removeTypingUser(payload.conversation_id, payload.user_id);
      delete typingTimeoutsRef.current[key];
    }, 5000);
  }, []);

  const handleStoppedTyping = useCallback((payload: SocketTypingPayload) => {
    useMessagingStore.getState().removeTypingUser(payload.conversation_id, payload.user_id);
    const key = `${payload.conversation_id}:${payload.user_id}`;
    if (typingTimeoutsRef.current[key]) {
      clearTimeout(typingTimeoutsRef.current[key]);
      delete typingTimeoutsRef.current[key];
    }
  }, []);

  // Clean up typing timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      typingTimeoutsRef.current = {};
    };
  }, []);

  const { socketRef, isConnected } = useSocketFactory({
    namespace: '',
    displayName: 'Messaging',
    tokenSource: 'authStore',
    authToken: token,
    extraAuth: activeTenant?.id ? { tenantId: activeTenant.id } : undefined,
    enabled: !!token && !!activeTenant?.id,
    showToasts: true,
    onConnect: (socket) => {
      useMessagingStore.getState().setConnected(true);
      const activeConvo = useMessagingStore.getState().activeConversationId;
      if (activeConvo) {
        console.log('[Messaging] Auto-joining conversation:', activeConvo);
        socket.emit('join:conversation', activeConvo);
      }
    },
    onDisconnect: () => {
      useMessagingStore.getState().setConnected(false);
    },
    onConnectError: (error) => {
      console.error('[Messaging] Connection error:', error.message);
      useMessagingStore.getState().setConnected(false);
    },
    socketOptions: {
      // Must not be finite. This provider is mounted in MainLayout, so it
      // usually wins socket.io's per-origin Manager cache — its reconnection
      // budget is then inherited by /map, /forms and every other namespace,
      // whose own options are discarded. A finite budget exhausted during a
      // field outage kills real-time updates app-wide until a page reload.
      reconnectionAttempts: Infinity,
      reconnectionDelayMax: 10000,
    },
    events: {
      'message:new': (payload: SocketMessagePayload) => handleMessageNew(payload, stableSocketRef),
      'message:delivered': handleMessageDelivered,
      'message:updated': handleMessageUpdated,
      'message:deleted': handleMessageDeleted,
      'user:typing': handleTyping,
      'user:stopped_typing': handleStoppedTyping,
      'message:read': handleMessageRead,
      'reaction:added': handleReactionAdded,
      'reaction:removed': handleReactionRemoved,
      'conversation:updated': handleConversationUpdated,
      'presence:online_users': (userIds: string[]) => {
        useMessagingStore.getState().setOnlineUsers(userIds);
      },
      'presence:user_online': (userId: string) => {
        useMessagingStore.getState().addOnlineUser(userId);
      },
      'presence:user_offline': (userId: string) => {
        useMessagingStore.getState().removeOnlineUser(userId);
      },
      'error': (error: { message: string }) => {
        console.error('[Messaging] Socket error:', error.message);
        toast.error(error.message || 'Message failed to send');
      },
    },
  });

  // Keep stableSocketRef in sync so event handlers can use it
  stableSocketRef.current = socketRef.current;

  // -------------------------------------------------------------------------
  // Action callbacks
  // -------------------------------------------------------------------------

  const joinConversation = useCallback(
    (conversationId: string) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('join:conversation', conversationId);
      }
    },
    [socketRef],
  );

  const leaveConversation = useCallback(
    (conversationId: string) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('leave:conversation', conversationId);
      }
    },
    [socketRef],
  );

  const sendMessage = useCallback(
    (data: {
      conversation_id: string;
      content?: string;
      type?: 'text' | 'file' | 'system';
      reply_to_id?: string;
      metadata?: Record<string, unknown>;
    }) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('message:send', data);
      } else {
        toast.error('Not connected — message not sent');
      }
    },
    [socketRef],
  );

  const editMessage = useCallback(
    (data: { message_id: string; conversation_id: string; content: string }) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('message:edit', data);
      } else {
        toast.error('Not connected — edit not saved');
      }
    },
    [socketRef],
  );

  const deleteMessage = useCallback(
    (data: { message_id: string; conversation_id: string }) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('message:delete', data);
      }
    },
    [socketRef],
  );

  const startTyping = useCallback(
    (conversationId: string) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('typing:start', conversationId);
      }
    },
    [socketRef],
  );

  const stopTyping = useCallback(
    (conversationId: string) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('typing:stop', conversationId);
      }
    },
    [socketRef],
  );

  const markAsRead = useCallback(
    (data: { message_id: string; conversation_id: string }) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('message:read', data);
      }
    },
    [socketRef],
  );

  const markConversationAsRead = useCallback(
    (conversationId: string) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('conversation:read', conversationId);
      }
    },
    [socketRef],
  );

  const toggleReaction = useCallback(
    (data: { message_id: string; conversation_id: string; emoji: string }) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('reaction:toggle', data);
      }
    },
    [socketRef],
  );

  const value = useMemo<MessagingSocketContextValue>(
    () => ({
      socket: socketRef.current,
      joinConversation,
      leaveConversation,
      sendMessage,
      editMessage,
      deleteMessage,
      startTyping,
      stopTyping,
      markAsRead,
      markConversationAsRead,
      toggleReaction,
    }),
    // socketRef is a stable ref, but isConnected changes when the socket
    // connects/disconnects — include it so consumers get an updated `socket`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isConnected,
      joinConversation,
      leaveConversation,
      sendMessage,
      editMessage,
      deleteMessage,
      startTyping,
      stopTyping,
      markAsRead,
      markConversationAsRead,
      toggleReaction,
    ],
  );

  return (
    <MessagingSocketContext.Provider value={value}>
      {children}
    </MessagingSocketContext.Provider>
  );
}
